/**
 * PRIMITIVES — ported from the aesthetic reference (§11, L2026–2140). Owner: S2.
 *
 * Hover = light arriving (raking light via .ld-rake + useLightField on a root),
 * active = light at rest, select = the land answers. Styling lives in
 * src/design/css (S1); these components only emit the reference markup.
 */
import { useRef, type ButtonHTMLAttributes, type CSSProperties, type JSX, type KeyboardEvent, type ReactNode } from "react";
import { Icon, type IconName } from "./icons";
import { CSS_ESC, useIndicator } from "./hooks";

export const cx = (...a: Array<string | false | null | undefined>): string => a.filter(Boolean).join(" ");

/** CSSProperties plus custom properties (`--s`, `--st-dot`, …). */
type CSSVars = CSSProperties & { [k: `--${string}`]: string | number | undefined };

/* ── Button ─────────────────────────────────────────────────────────── */

// The quiet button's underline: a flat line that a small wave draws across on hover.
const WAVE_D = (() => {
  let d = "M0 3.5";
  for (let x = 0; x < 100; x += 10) {
    const cy = (x / 10) % 2 ? 6.4 : 0.6;
    d += ` C${x + 3.3} ${cy} ${x + 6.7} ${cy} ${x + 10} 3.5`;
  }
  return d;
})();

export type ButtonVariant = "sun" | "glass" | "earth" | "paper" | "quiet";
export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  size?: "sm" | "lg";
  icon?: IconName | (string & {});
  iconRight?: IconName | (string & {});
  busy?: boolean;
  block?: boolean;
  children?: ReactNode;
  /** Default "button". */
  type?: "button" | "submit" | "reset";
}

export function Button({ variant = "sun", size, icon, iconRight, busy, block, className, children, type = "button", ...rest }: ButtonProps): JSX.Element {
  const quiet = variant === "quiet";
  const is = size === "sm" ? 16 : 18;
  return (
    <button
      type={type}
      className={cx("ld-btn", `ld-btn--${variant}`, size && `ld-btn--${size}`, block && "ld-btn--block", !quiet && "ld-rake", busy && "is-busy", className)}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <Spinner /> : icon && <Icon name={icon} size={is} />}
      <span className="ld-btn__label">{children}</span>
      {iconRight && <Icon name={iconRight} size={is} />}
      {quiet && (
        <svg className="ld-wave" viewBox="0 0 100 7" preserveAspectRatio="none" aria-hidden="true">
          <path className="w-base" d="M0 3.5H100" />
          <path className="w-draw" d={WAVE_D} pathLength="1" />
        </svg>
      )}
    </button>
  );
}

/* ── Spinner ────────────────────────────────────────────────────────── */

/** Two broken tree rings turning. Colour = currentColor. */
export function Spinner({ size = 16, className }: { size?: number; className?: string }): JSX.Element {
  return (
    <svg className={cx("ld-spin", className)} viewBox="0 0 20 20" width={size} height={size} aria-hidden="true">
      <circle cx="10" cy="10" r="7.5" strokeDasharray="12 4 20 6" opacity=".9" />
      <circle cx="10" cy="10" r="3.8" strokeDasharray="6 3 9 4" opacity=".6" />
    </svg>
  );
}

/* ── IconButton ─────────────────────────────────────────────────────── */

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  icon: IconName | (string & {});
  label: string;
  variant?: "glass" | "paper";
  solo?: boolean;
  /** Tooltip side (shows the label after a calm delay). null = no tooltip. */
  tip?: "down" | "up" | "left" | "right" | null;
  pressed?: boolean;
  badge?: number | string | null;
  size?: number;
  iconSize?: number;
}

export function IconButton({
  icon,
  label,
  variant = "glass",
  solo,
  tip = "down",
  pressed,
  badge,
  size = 40,
  iconSize = 20,
  className,
  style,
  ...rest
}: IconButtonProps): JSX.Element {
  const st: CSSVars = { "--s": `${size}px`, ...style };
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      className={cx("ld-ibtn", `ld-ibtn--${variant}`, solo && "ld-ibtn--solo", "ld-rake", className)}
      style={st}
      {...rest}
    >
      <Icon name={icon} size={iconSize} />
      {badge ? (
        <span className="ld-badge" key={String(badge)}>
          {badge}
        </span>
      ) : null}
      {tip && (
        <span className={cx("ld-tip", tip === "up" && "ld-tip--up", tip === "left" && "ld-tip--left", tip === "right" && "ld-tip--right")} aria-hidden="true">
          {label}
        </span>
      )}
    </button>
  );
}

/* ── Chip ───────────────────────────────────────────────────────────── */

// Counts sit after the label in a quieter weight; .ld-num gives tabular figures.
// Inline so the rule holds whatever the partials do; CSS may refine .ld-chip__count / .ld-island__count.
const COUNT_STYLE: CSSProperties = { fontWeight: 400 };

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  selected?: boolean;
  glass?: boolean;
  /** The small light that glows sun when selected. Default: shown unless an icon is given. */
  dot?: boolean;
  /** Rendered after the label in a quieter weight, tabular figures. */
  count?: number;
  icon?: IconName | (string & {});
  children?: ReactNode;
}

export function Chip({ selected, glass, dot, count, icon, className, children, ...rest }: ChipProps): JSX.Element {
  const showDot = dot ?? !icon;
  return (
    <button type="button" aria-pressed={!!selected} className={cx("ld-chip", glass && "ld-chip--glass", "ld-rake", className)} {...rest}>
      {showDot && <i className="ld-chip__dot" />}
      {icon && <Icon name={icon} size={15} className="ld-chip__icon" />}
      {children}
      {count != null && (
        <>
          {" "}
          <span className="ld-chip__count ld-num" style={COUNT_STYLE}>{count}</span>
        </>
      )}
    </button>
  );
}

/* ── Status ─────────────────────────────────────────────────────────── */

/** Status keys: aesthetic unit statuses + place statuses. */
export type StatusKey =
  | "available" | "limited" | "reserved" | "sold" | "next" | "civic"
  | "existing" | "upcoming" | "proposed" | "under-construction" | "indicative";

export interface StatusDef {
  label: string;
  dot: string;
  ink: string;
  lit: string;
}

export const STATUS: Record<StatusKey, StatusDef> = {
  // units (aesthetic)
  available: { label: "Available", dot: "var(--sage)", ink: "var(--sage-ink)", lit: "var(--sage-lit)" },
  limited: { label: "Few remaining", dot: "var(--sun)", ink: "var(--mud)", lit: "var(--sand)" },
  reserved: { label: "Reserved", dot: "var(--sand)", ink: "var(--mud)", lit: "var(--sand)" },
  sold: { label: "Sold", dot: "var(--terracotta)", ink: "var(--terracotta-ink)", lit: "var(--terracotta-lit)" },
  next: { label: "Next phase", dot: "var(--sky)", ink: "var(--forest)", lit: "var(--sky-lit)" },
  civic: { label: "Community", dot: "var(--stone)", ink: "var(--mud)", lit: "var(--stone)" },
  // places
  existing: { label: "Existing", dot: "var(--sage)", ink: "var(--sage-ink)", lit: "var(--sage-lit)" },
  // Status tags never take sun hues (spec decision 6): upcoming uses the `next` style (§B.1, §C row 2).
  upcoming: { label: "Upcoming", dot: "var(--sky)", ink: "var(--forest)", lit: "var(--sky-lit)" },
  proposed: { label: "Proposed", dot: "var(--sky)", ink: "var(--forest)", lit: "var(--sky-lit)" },
  "under-construction": { label: "Under construction", dot: "var(--sand)", ink: "var(--mud)", lit: "var(--sand)" },
  indicative: { label: "Indicative", dot: "var(--sky)", ink: "var(--forest)", lit: "var(--sky-lit)" },
};

export const isStatusKey = (s: string): s is StatusKey => Object.prototype.hasOwnProperty.call(STATUS, s);

// Dot shapes for the place statuses. The unit statuses are shaped by controls.css
// ([data-s] rules); these inline fallbacks keep the new ones right on their own.
const DIAMOND: CSSProperties = { background: "transparent", boxShadow: "inset 0 0 0 1.5px var(--st-dot)", borderRadius: 2, transform: "rotate(45deg)" }; // like "next"
const SHAPE: Partial<Record<StatusKey, CSSProperties>> = {
  upcoming: DIAMOND,
  proposed: DIAMOND,
  indicative: { background: "transparent", border: "1.5px dashed var(--st-dot)", boxSizing: "border-box" }, // sky dashed ring
};

export function Status({ s, className }: { s: StatusKey | (string & {}); className?: string }): JSX.Element {
  const key: StatusKey = isStatusKey(s) ? s : "available";
  const st = STATUS[key];
  const vars: CSSVars = { "--st-dot": st.dot, "--st-ink": st.ink, "--st-lit": st.lit };
  return (
    <span className={cx("ld-status", className)} data-s={key} style={vars}>
      <i style={SHAPE[key]} />
      {st.label}
    </span>
  );
}

/* ── Switch ─────────────────────────────────────────────────────────── */

export interface SwitchProps {
  checked: boolean;
  onChange?: (v: boolean) => void;
  label?: ReactNode;
  className?: string;
  disabled?: boolean;
}

export function Switch({ checked, onChange, label, className, disabled }: SwitchProps): JSX.Element {
  return (
    <button type="button" role="switch" aria-checked={!!checked} disabled={disabled} className={cx("ld-switch", className)} onClick={() => onChange?.(!checked)}>
      <span className="ld-switch__track">
        <span className="ld-switch__knob" />
      </span>
      {label && <span>{label}</span>}
    </button>
  );
}

/* ── Segmented ──────────────────────────────────────────────────────── */

export interface SegItem {
  key: string;
  label?: string;
  icon?: IconName | (string & {});
  title?: string;
  sep?: boolean;
  count?: number;
}
export interface SegmentedProps {
  items: SegItem[];
  value: string | null;
  onChange: (key: string) => void;
  variant?: "glass" | "day" | "paper";
  size?: "sm";
  col?: boolean;
  label: string;
  role?: "radiogroup" | "tablist" | "toolbar";
  className?: string;
  style?: CSSProperties;
}

// Island + Segmented share one engine: items, a sunrise, arrow keys.
export function Segmented({ items, value, onChange, variant = "glass", size, col, label, role = "radiogroup", className, style }: SegmentedProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const box = useIndicator(ref, value);
  const nav = items.filter((it) => !it.sep);
  const roving = role !== "toolbar";
  const hasActive = nav.some((it) => it.key === value);

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!nav.length) return;
    const keys = col ? ["ArrowUp", "ArrowDown"] : ["ArrowLeft", "ArrowRight"];
    const i = nav.findIndex((it) => it.key === value);
    let n: SegItem | undefined;
    if (e.key === "Home") n = nav[0];
    else if (e.key === "End") n = nav[nav.length - 1];
    else if (keys.includes(e.key)) {
      const from = i < 0 ? (e.key === keys[1] ? -1 : 0) : i;
      n = nav[(from + (e.key === keys[1] ? 1 : nav.length - 1)) % nav.length];
    }
    if (!n) return;
    e.preventDefault();
    onChange(n.key);
    ref.current?.querySelector<HTMLElement>(`[data-key="${CSS_ESC(n.key)}"]`)?.focus();
  };

  const itemRole = role === "radiogroup" ? "radio" : role === "tablist" ? "tab" : undefined;
  return (
    <div
      ref={ref}
      role={role}
      aria-label={label}
      aria-orientation={role !== "radiogroup" && col ? "vertical" : undefined}
      onKeyDown={onKey}
      className={cx(
        "ld-island",
        variant === "day" ? "ld-island--day ld-glass--day" : variant === "paper" ? "ld-island--day ld-island--paper" : "ld-glass",
        size === "sm" && "ld-island--sm",
        col && "ld-island--col",
        className,
      )}
      style={style}
    >
      <Sunrise box={box} />
      {items.map((it, idx) => {
        if (it.sep) return <span key={it.key} className="ld-island__sep" />;
        const on = value === it.key;
        const first = !hasActive && nav[0] === items[idx];
        return (
          <button
            key={it.key}
            type="button"
            data-key={it.key}
            role={itemRole}
            aria-checked={role === "radiogroup" ? on : undefined}
            aria-selected={role === "tablist" ? on : undefined}
            aria-pressed={role === "toolbar" ? on : undefined}
            tabIndex={roving && !on && !first ? -1 : 0}
            className="ld-island__item ld-rake"
            onClick={() => onChange(it.key)}
            title={it.title}
            aria-label={it.label ? undefined : it.title}
          >
            {it.icon && <Icon name={it.icon} size={size === "sm" ? 16 : 19} />}
            {it.label && <span className={it.icon ? "ld-island__label" : undefined}>{it.label}</span>}
            {it.count != null && (
              <>
                {" "}
                <span className="ld-island__count ld-num" style={COUNT_STYLE}>{it.count}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ── Sunrise ────────────────────────────────────────────────────────── */

export interface SunriseBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The active indicator: a sun that rises behind the chosen item, with a ridge line under it. */
export function Sunrise({ box }: { box: SunriseBox | null }): JSX.Element {
  return (
    <span
      className={cx("ld-sunrise", box && "is-ready")}
      aria-hidden="true"
      style={box ? { transform: `translate(${box.x}px, ${box.y}px)`, width: box.w, height: box.h } : undefined}
    >
      <span className="ld-sunrise__sun" key={box ? `${box.x}:${box.y}` : "none"} />
      <span className="ld-sunrise__line" key={box ? `l${box.x}:${box.y}` : "lnone"} />
    </span>
  );
}
